// Service Worker Integration Component
// Add this component to your app to enable service worker functionality with toast notifications

import { useEffect } from 'react';
import { usePWA } from 'utils/pwa-hooks';
import { userState } from 'state';
import { useRecoilState } from 'recoil';
import { EnumUserRole } from 'lib/graphql';

// Routes that should be pre-loaded based on user navigation patterns
const ROUTE_PRELOAD_MAP: Record<EnumUserRole, string[]> = {
  [EnumUserRole.Admin]: [
    '/processor/calendar',
    '/processor/agenda',
    '/processor/customers',
    '/processor/jobs',
  ],
  [EnumUserRole.Vendor]: [
    '/processor/calendar',
    '/processor/agenda',
    '/processor/customers',
    '/processor/jobs',
  ],
  [EnumUserRole.Basic]: ['/jobs', '/chat'],
};

// Preload routes by creating invisible link elements with prefetch
const preloadRoutes = (routes: string[]) => {
  routes.forEach((route) => {
    // Check if route is already preloaded
    const existingLink = document.querySelector(`link[href="${route}"]`);
    if (existingLink) return;

    // Create prefetch link
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = route;
    link.as = 'document';

    // Add to document head
    document.head.appendChild(link);

    // eslint-disable-next-line no-console
    console.log(`[PWA] Preloading route: ${route}`);
  });
};

export function ServiceWorkerProvider() {
  const [user] = useRecoilState(userState);

  // Initialize PWA functionality
  usePWA({
    onNeedRefresh() {
      // eslint-disable-next-line no-console
      console.log('[PWA] Update available - user will be notified via toast');
    },
    onOfflineReady() {
      // eslint-disable-next-line no-console
      console.log('[PWA] App is ready for offline use');
    },
  });

  // Preload routes based on user role
  useEffect(() => {
    if (!user?.role) return undefined;

    const routesToPreload = ROUTE_PRELOAD_MAP[user.role];

    if (routesToPreload?.length > 0) {
      // Delay preloading to avoid interfering with initial page load
      const timeoutId = setTimeout(() => {
        preloadRoutes(routesToPreload);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [user?.role]);

  // This component doesn't render anything - it just initializes PWA functionality
  return null;
}
